import cx from 'classnames';
import { t } from 'i18next';
import { useMemo } from 'react';
import { getExtendedSpecRoleFromSpec, getRoleIconForSpec } from '../../../../../../shared/common/wow';
import { Avatar } from '../../../../../../shared/frontend/components/avatar/Avatar';
import { Stack } from '../../../../../../shared/frontend/components/stack/Stack';
import { DataTable } from '../../../../../../shared/frontend/components/tables/dataTable/DataTable';
import { IDataTableProps, TColumn } from '../../../../../../shared/frontend/components/tables/types';
import { Typography } from '../../../../../../shared/frontend/components/typography';
import { processCharacterSpecialItems } from '../../../../../../shared/frontend/scripts/wow/gearHelpers';
import { categorizeSpentLoadoutTalents } from '../../../../../../shared/common/talentHelpers';
import { TalentIcon } from '../../../../../../shared/frontend/scripts/wow/components/TalentIcon';
import { WowheadItemLink, WowheadLink } from '../../../../../../shared/frontend/scripts/wow/components/WowheadLink';
import WowIcon from '../../../../../../shared/frontend/scripts/wow/components/WowIcon';
import { TGuildSummary } from '../../../../shared/types/guildTypes';
import {
  IRaidEncounterRosterMemberCharacter,
  TEncounterBossKillRosterMember,
  TRaidEncounterSummary,
  TRaidEncounterVideoListSummary,
  TRaidSummaryWithoutBosses
} from '../../../../shared/types/raidTypes';
import { getWowheadTalentCalcUrl } from '../../../../../../shared/frontend/scripts/talents/utils/getWowheadTalentCalcUrl';
import { buildArmoryTalentCalculatorUrl } from '../../../../shared/utils';
import { CharacterCallout } from '../../character/components/CharacterCallout';
import { CharacterRosterName } from '../../components/container/characterRosterName';
import { CorruptionDetailsTooltip } from '../../components/presentational/corruptionTooltip';
import { TalentLoadoutActionsMenu } from '../../components/presentational/TalentLoadoutActionsMenu';
import { TrackedLink } from '../../../../../../shared/frontend/components/link';
import { NumberOrNotAvailable } from '../../components/presentational/numberOrNotAvailable';
import { ComplexTooltip, SimpleTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import {
  getModifiedArtifactTraits,
  shouldShowArtifactTraits,
  shouldShowAzeriteGear,
  shouldShowCorruption,
  shouldShowCovenant,
  shouldShowDFTalents,
  shouldShowDominationItems,
  shouldShowDominationShards,
  shouldShowEssences,
  shouldShowLegendaries,
  shouldShowTalents,
  shouldShowTier,
  sortRoster
} from '../utils';
import _ from 'lodash';
import { ExtendedSpecRole, SpecRole } from '../../../../../../shared/types/wowTypes';
import { EXTENDED_ROLES_TO_PRIMARY_ROLE_MAP } from '../../../../../../shared/common/wow';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { encodeSearchParams } from '../../../../../../shared/frontend/libs/utils';
import { useSiteMeta } from '../../../../../../shared/frontend/scripts/selectors/global';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { isFindVideosBySpecRaidAllowed } from '../../../../shared/videoSearchAccess';
import { SpecStatisticsActivity } from '../../specs/types/surfaces';

interface IEncounterKillRosterTableProps
  extends Omit<IDataTableProps<TTableRecord>, 'columns' | 'data' | 'expandable'> {
  embed?: boolean;
  className?: string;
  enableRowHover?: boolean;
  raid: TRaidSummaryWithoutBosses;
  boss: TRaidEncounterSummary;
  difficulty: string;
  guild: TGuildSummary;
  roster?: TEncounterBossKillRosterMember[];
  videos?: TRaidEncounterVideoListSummary[];
  // By Default all columns are visible
  visibleColumns?: EncounterKillRosterTableColumn[];
  isLoading?: boolean;
  viewConfig?: TEncounterKillRosterTableViewConfig;
  setGuildBossKillVideo?: (bossKillKey: string, video: TRaidEncounterVideoListSummary) => void;
  bossKillKey?: string;
  expandable?: boolean;
  summaries: any;
  validTiers: string[];
}

type TEncounterKillRosterTableViewConfig = {};

export enum EncounterKillRosterTableColumn {
  Expand = 'expand',
  Index = 'index',
  Character = 'character',
  HeroSpec = 'heroSpec',
  Role = 'role',
  ItemLevel = 'itemLevel',
  Covenant = 'covenant',
  SoulbindConduits = 'soulbindConduits',
  ArtifactTraits = 'artifactTraits',
  Legendaries = 'legendaries',
  Domination = 'domination',
  Shards = 'shards',
  HoA = 'hoa',
  Corruption = 'corruption',
  Essences = 'essences',
  AzeritePowers = 'azeritePowers',
  Videos = 'videos',
  Trinkets = 'trinkets',
  LegacyTalents = 'legacy-talents',
  ClassTalents = 'classTalents',
  SpecTalents = 'specTalents',
  HeroTalents = 'heroTalents',
  LoadoutCalculator = 'loadoutCalculator',
  AllTiers = 'all-tiers',
  HighestTier = 'highest-tier'
}

export const getEncounterKillRosterTableColumnLabel = (column: EncounterKillRosterTableColumn, short = false) => {
  return t(`composition-compare:table.columns.${column}.${short ? 'short_name' : 'name'}`);
};

type TTableRecord = TEncounterBossKillRosterMember & {
  index: number;
  classTalents: any[];
  specTalents: any[];
  heroTalents: any[];
  specialItems: any;
};

export function EncounterKillRosterTable({
  visibleColumns = Object.values(EncounterKillRosterTableColumn),
  roster = [],
  embed,
  videos = [],
  className,
  enableRowHover,
  raid,
  boss,
  difficulty,
  setGuildBossKillVideo,
  bossKillKey,
  isLoading,
  viewConfig: viewConfigProp,
  expandable,
  summaries,
  validTiers,
  ...restProps
}: IEncounterKillRosterTableProps) {
  const siteMeta = useSiteMeta();
  const supportsFindVideosBySpec = isFindVideosBySpecRaidAllowed(raid.slug);

  const hasAnyDFLoadouts = !!roster.find((ch) => !!ch.character.talentLoadout);
  const hasAnyHeroTalents = hasAnyDFLoadouts && !!roster.find((ch) => ch.character.talentLoadout?.heroSubTreeId);

  const getTooltipContent = (title: string, description: string) => {
    return (
      <span>
        <p className="slds-text-body--small">{title}</p>
        <p>
          <i className="slds-text-body--small text-muted">{description}</i>
        </p>
      </span>
    );
  };

  // const getHeaderColumnTooltip = (column: EncounterKillRosterTableColumn) => {
  //   return getTooltipContent(
  //     t(`composition-compare:table.columns.${column}.tooltip.title`),
  //     t(`composition-compare:table.columns.${column}.tooltip.description`)
  //   );
  // };

  const buildCharacterUrl = (character: IRaidEncounterRosterMemberCharacter) =>
    resolveRoutePath(namedRoutes.CharacterDetails, {
      region: character.region.slug,
      realm: character.realm.slug,
      character: character.name
    });

  const buildFindVideosFromSpecUrl = (character: IRaidEncounterRosterMemberCharacter) =>
    `${resolveRoutePath(namedRoutes.Search)}?${encodeSearchParams({
      type: 'raid_encounters',
      raid: [{ eq: raid.slug }],
      raidBossId: [{ eq: boss.encounterId }],
      videoSpecIds: character.spec?.id ? [{ eq: character.spec.id }] : undefined,
      sort: {
        difficulty: 'desc',
        pullEndedAt: 'desc'
      }
    })}`;

  const buildTalentCompareUrl = (character: IRaidEncounterRosterMemberCharacter) => {
    const loadoutText = character.talentLoadout?.loadoutText;
    const dbcIndexVersion = character.talentLoadout?.dbcIndexVersion;

    if (!loadoutText) {
      return undefined;
    }

    const searchParams = new URLSearchParams();
    searchParams.set('loadoutA', loadoutText);
    searchParams.set('activity', SpecStatisticsActivity.Raid);
    searchParams.set('raid', raid.slug);
    searchParams.set('difficulty', difficulty);
    searchParams.set('encounterSlug', boss.slug);

    if (dbcIndexVersion) {
      searchParams.set('dbcIndexVersion', dbcIndexVersion);
    }

    return `${namedRoutes.SpecTalentCompare}?${searchParams.toString()}`;
  };

  const columns = useMemo<TColumn<TTableRecord>[]>(() => {
    let tiersToShow: string[] = [];

    if (validTiers) {
      if (visibleColumns.includes(EncounterKillRosterTableColumn.HighestTier)) {
        tiersToShow = _.compact([_.first([...validTiers].sort((a, b) => Number(b) - Number(a)))!]);
      }

      if (visibleColumns.includes(EncounterKillRosterTableColumn.AllTiers)) {
        tiersToShow = [...validTiers].sort((a, b) => Number(b) - Number(a));
      }
    }

    const baseColumns: TColumn<TTableRecord>[] = [
      {
        header: '',
        accessor: EncounterKillRosterTableColumn.Index,
        width: sortRoster.length >= 10 ? '28px' : '20px',
        headerStyle: { padding: '0 4px' },
        cellRender: (_, { dataTableRowIndex }) => (
          <Typography.Text color="muted">{dataTableRowIndex + 1}</Typography.Text>
        )
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Role),
        accessor: EncounterKillRosterTableColumn.Role,
        sortable: true,
        sortingFn: (a, b) => {
          const specA = a.character.spec;
          const specB = b.character.spec;

          const roleA = getExtendedSpecRoleFromSpec(specA);
          const roleB = getExtendedSpecRoleFromSpec(specB);

          const primaryRoleA = EXTENDED_ROLES_TO_PRIMARY_ROLE_MAP[roleA];
          const primaryRoleB = EXTENDED_ROLES_TO_PRIMARY_ROLE_MAP[roleB];

          const rolePriority: Record<SpecRole | ExtendedSpecRole, number> = {
            [ExtendedSpecRole.Tank]: 1,
            [ExtendedSpecRole.Healer]: 2,
            [SpecRole.DPS]: 3,
            [ExtendedSpecRole.MeleeDps]: 3.1,
            [ExtendedSpecRole.RangedDps]: 3.2
          };

          const primaryComparison = rolePriority[primaryRoleA] - rolePriority[primaryRoleB];

          if (primaryComparison !== 0) {
            return primaryComparison;
          }

          if (rolePriority[roleA] !== rolePriority[roleB]) {
            return rolePriority[roleA] - rolePriority[roleB];
          }

          // once roles match we sort by spec to group similar specs
          return specA.id - specB.id;
        },
        cellRender: ({ character }) => (
          <Stack alignItems="center">
            <Avatar contentBox size={18} border>
              <span className={`icon-role icon-x-small ${getRoleIconForSpec(character.spec)}`} />
            </Avatar>
            <span className="spec-role-name">
              {t(`common:roles.${getExtendedSpecRoleFromSpec(character.spec)}.short_name`)}
            </span>
          </Stack>
        )
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Character),
        align: 'left',
        accessor: EncounterKillRosterTableColumn.Character,
        sortable: true,
        sortingFn: (a, b) => {
          return a.character.name.localeCompare(b.character.name);
        },
        cellRender: ({ character }) => <CharacterRosterName character={character} />
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.HeroSpec),
        align: 'left',
        accessor: EncounterKillRosterTableColumn.HeroSpec,
        sortable: true,
        sortingFn: (a, b) => {
          return (a.character.talentLoadout?.heroSubTreeId || 0) - (b.character.talentLoadout?.heroSubTreeId || 0);
        },
        cellRender: ({ character }) => {
          const hasHeroSubTree =
            character.talentLoadout && character.talentLoadout.heroSubTreeId && (siteMeta as any).heroSubTrees;

          const heroSubTree = hasHeroSubTree
            ? (siteMeta as any).heroSubTrees[character.talentLoadout!.heroSubTreeId]
            : null;

          if (!heroSubTree) {
            return null;
          }

          return (
            <ComplexTooltip
              placement="top"
              tooltip={
                <div>
                  <div className="text-bold">{heroSubTree.name}</div>
                </div>
              }
              className="slds-text-align--center"
            >
              <img
                className="rio-wow-icon rio-herospec-icon rio-wow-icon--small rio-wow-icon--shape-circle"
                src={asset(`/images/site/hero_specs/herospec_${heroSubTree.slug}.png`)}
              />
            </ComplexTooltip>
          );
        }
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Videos),
        accessor: EncounterKillRosterTableColumn.Videos,
        align: 'center',
        cellRender: ({ character }) => (
          <VideoColumn
            videos={videos}
            character={character}
            setGuildBossKillVideo={setGuildBossKillVideo}
            bossKillKey={bossKillKey}
          />
        ),
        hidden: !videos.length
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.ItemLevel, true),
        accessor: EncounterKillRosterTableColumn.ItemLevel,
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.ItemLevel),
        align: 'center',
        sortable: true,
        sortingFn: (a, b) => {
          return a.character.itemLevelEquipped - b.character.itemLevelEquipped;
        },
        cellRender: ({ character }) => (
          <>
            {embed ? (
              <SimpleTooltip tooltip={character.itemLevelEquipped} hideTooltip={!character.itemLevelEquipped}>
                <NumberOrNotAvailable num={character.itemLevelEquipped.toFixed(1)} />
              </SimpleTooltip>
            ) : (
              <NumberOrNotAvailable num={character.itemLevelEquipped.toFixed(1)} />
            )}
          </>
        )
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Covenant),
        accessor: EncounterKillRosterTableColumn.Covenant,
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Covenant),
        hidden: !shouldShowCovenant(raid.slug),
        align: 'center',
        cellRender: ({ character }) => (
          <>
            {character.covenant ? (
              <SimpleTooltip
                tooltip={
                  <div>
                    <div>
                      <b>{character.covenant.name}</b>
                    </div>
                    <div className="text-muted">
                      <i>{character.covenant.soulbind.name}</i>
                    </div>

                    <div>
                      {character.covenant?.soulbind && character.covenant.soulbind.traits.length > 0 && (
                        <span className="slds-m-right--x-small">
                          {character.covenant.soulbind.traits.map((trait: any) => (
                            <WowheadLink key={trait.spell.id} type="spell" id={trait.spell.id}>
                              <WowIcon icon={trait.spell.icon} size="small" shape="circle" />
                            </WowheadLink>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                }
              >
                <WowIcon icon={character.covenant.icon} size="small" />
              </SimpleTooltip>
            ) : (
              <Typography.Text color="muted">-</Typography.Text>
            )}
          </>
        )
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.SoulbindConduits),
        accessor: EncounterKillRosterTableColumn.SoulbindConduits,
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.SoulbindConduits),
        hidden: !shouldShowCovenant(raid.slug),
        align: 'center',
        cellRender: ({ character }) => (
          <>
            {!character.covenant?.soulbind && <Typography.Text color="muted">-</Typography.Text>}
            {character.covenant?.soulbind && character.covenant.soulbind.conduits.length > 0 && (
              <>
                {character.covenant.soulbind.conduits.map((conduit: any) => (
                  <WowheadLink key={conduit.spell.id} type="spell" id={conduit.spell.id} rank={conduit.rank - 1}>
                    <WowIcon icon={conduit.spell.icon} size="small" shape="octagon" />
                  </WowheadLink>
                ))}
              </>
            )}
          </>
        )
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.ArtifactTraits),
        accessor: EncounterKillRosterTableColumn.ArtifactTraits,
        hidden: !shouldShowArtifactTraits(raid.slug),
        align: 'center',
        cellRender: ({ character }) => (
          <NumberOrNotAvailable num={getModifiedArtifactTraits(raid.slug, character.artifactTraits)} />
        )
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Legendaries),
        accessor: EncounterKillRosterTableColumn.Legendaries,
        hidden: !shouldShowLegendaries(raid.slug),
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Legendaries),
        align: 'center',
        cellRender: ({ specialItems }) => {
          return (
            <>
              {specialItems.legendaries.length > 0 ? (
                <Stack wrap spacingInRems={1} justifyContent="center">
                  {specialItems.legendaries.map((item: any) => (
                    <WowheadItemLink key={item.item_id} item={item}>
                      <WowIcon size="small" icon={item.icon} />
                    </WowheadItemLink>
                  ))}
                </Stack>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },
      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Domination),
        accessor: EncounterKillRosterTableColumn.Domination,
        hidden: !shouldShowDominationItems(raid.slug) || !summaries?.validShards,
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Domination),
        align: 'center',
        cellRender: ({ specialItems }) => {
          return (
            <>
              {specialItems.dominationSocketItems.length > 0 ? (
                <Stack wrap spacingInRems={1} justifyContent="center">
                  {specialItems.dominationSocketItems.map((item: any) => (
                    <WowheadItemLink key={item.item_id} item={item}>
                      <WowIcon size="small" icon={item.icon} />
                    </WowheadItemLink>
                  ))}
                </Stack>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Shards),
        accessor: EncounterKillRosterTableColumn.Shards,
        hidden: !shouldShowDominationShards(raid.slug) || !summaries?.validShards,
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Shards),
        align: 'center',
        cellRender: ({ specialItems }) => {
          return (
            <>
              {specialItems.dominationShards.length > 0 ? (
                <Stack wrap spacingInRems={1} justifyContent="center">
                  {specialItems.dominationShards.map((item: any) => (
                    <WowheadItemLink key={item.item_id} item={item}>
                      <WowIcon size="small" icon={item.icon} />
                    </WowheadItemLink>
                  ))}
                </Stack>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.HoA),
        accessor: EncounterKillRosterTableColumn.HoA,
        hidden: !shouldShowAzeriteGear(raid.slug),
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.HoA),
        align: 'center',
        cellRender: ({ specialItems, character }) => {
          return (
            <>
              {specialItems.heartLevel > 0 ? (
                <span>
                  <WowheadLink
                    className="rio-realm-link"
                    type="item"
                    id={character.items?.items?.neck?.item_id}
                    rel={`ilvl=${character.items?.items.neck.item_level}`}
                  >
                    {specialItems.heartLevel.toFixed(1)}
                  </WowheadLink>
                </span>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Corruption),
        accessor: EncounterKillRosterTableColumn.Corruption,
        hidden: !shouldShowCorruption(raid.slug),
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Corruption),
        align: 'center',
        cellRender: ({ character }) => <CorruptionDetailsTooltip corruption={character.items?.corruption} />
      },

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Essences),
        accessor: EncounterKillRosterTableColumn.Essences,
        hidden: !shouldShowEssences(raid.slug),
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Essences),
        align: 'center',
        cellRender: ({ character }) => {
          const specialItems = processCharacterSpecialItems(character.items?.items);
          return (
            <>
              {specialItems.essences && specialItems.essences.length > 0 ? (
                <span>
                  {specialItems.essences
                    .filter((essence: any) => essence.power)
                    .map((essence: any) => (
                      <span key={essence.slot} className="rio-spaced-element--x-small" style={{ position: 'relative' }}>
                        {essence.slot === 0 && (
                          <i
                            title="Major Power"
                            className="fa fa-star text-gold"
                            style={{ position: 'absolute', fontSize: 8, top: -5, left: -2, zIndex: 0 }}
                          />
                        )}

                        <WowheadLink key={essence.slot} type="azerite-essence-power" id={essence.power.id}>
                          <WowIcon
                            size="small"
                            icon={
                              essence.slot === 0
                                ? essence.power.majorPowerSpell.icon
                                : essence.power.minorPowerSpell.icon
                            }
                          />
                        </WowheadLink>
                      </span>
                    ))}
                </span>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.AzeritePowers),
        accessor: EncounterKillRosterTableColumn.AzeritePowers,
        hidden: !shouldShowAzeriteGear(raid.slug),
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.AzeritePowers),
        align: 'center',
        cellRender: ({ character }) => {
          const specialItems = processCharacterSpecialItems(character.items?.items);

          return (
            <>
              {specialItems.heartLevel > 0 ? (
                <span>
                  <WowheadLink
                    className="rio-realm-link"
                    type="item"
                    id={character.items?.items?.neck?.item_id}
                    rel={`ilvl=${character.items?.items.neck.item_level}`}
                  >
                    {specialItems.heartLevel.toFixed(1)}
                  </WowheadLink>
                </span>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.Trinkets),
        accessor: EncounterKillRosterTableColumn.Trinkets,
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.Trinkets),
        align: 'center',
        cellRender: ({ specialItems }) => {
          return (
            <>
              {specialItems.trinkets.length > 0 ? (
                <Stack spacingInRems={1} justifyContent="center">
                  {specialItems.trinkets.map((trinket: any, trinketIndex: number) => (
                    <WowheadItemLink key={trinketIndex} item={trinket}>
                      <WowIcon size="small" icon={trinket.icon} />
                    </WowheadItemLink>
                  ))}
                </Stack>
              ) : (
                <Typography.Text color="muted">-</Typography.Text>
              )}
            </>
          );
        }
      },

      ...(shouldShowTier(raid.slug)
        ? tiersToShow.map<TColumn<TTableRecord>>((tier) => ({
            header: t('composition-compare:table.columns.tier.name', {
              tier: tier.includes('_') ? tier.replace('_', '.') : tier
            }),
            accessor: `tier${tier}`,
            tooltip: getTooltipContent(
              t('composition-compare:table.columns.tier.tooltip.title', { tier }),
              t('composition-compare:table.columns.tier.tooltip.description')
            ),
            align: 'center',
            cellRender: ({ specialItems }) => {
              return (
                <>
                  {specialItems[`tier${tier}`] ? (
                    <Stack spacingInRems={2} alignItems="center" className="text-white" justifyContent="center">
                      <span className="tier-count">{specialItems[`tier${tier}`].length} pc</span>
                      <WowheadItemLink
                        item={specialItems[`tier${tier}`][0]}
                        className="rio-spaced-element--x-small"
                        setPieces={specialItems[`tier${tier}`]}
                      >
                        <WowIcon size="small" icon={specialItems[`tier${tier}`][0].icon} />
                      </WowheadItemLink>
                    </Stack>
                  ) : (
                    <Typography.Text color="muted">-</Typography.Text>
                  )}
                </>
              );
            }
          }))
        : []),

      {
        header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.LegacyTalents),
        accessor: EncounterKillRosterTableColumn.LegacyTalents,
        hidden: !shouldShowTalents(raid.slug),
        // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.LegacyTalents),
        align: 'center',
        cellRender: ({ character }) => {
          return (
            <>
              {character.talentsDetails && character.talents && (
                <ComplexTooltip
                  tooltip={
                    <div className="slds-text-align--center">
                      <div>Talents In Use When Scanned</div>
                      <div className="slds-m-vertical--x-small">
                        {character.talentsDetails.map((talent, talentIndex) => (
                          <WowIcon
                            key={talentIndex}
                            className="slds-m-horizontal--xx-small"
                            size="small"
                            icon={talent.icon}
                          />
                        ))}
                      </div>
                      <i>Click for talent calculator</i>
                    </div>
                  }
                  placement="top"
                >
                  <TrackedLink
                    as="talent_calculator"
                    href={buildArmoryTalentCalculatorUrl(character.class, character.spec, character.talents)}
                  >
                    <i className="fa fa-diagram-project text-white" />
                  </TrackedLink>
                </ComplexTooltip>
              )}
            </>
          );
        }
      },

      ...(shouldShowDFTalents(raid.slug) && hasAnyDFLoadouts
        ? ([
            {
              header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.ClassTalents),
              accessor: EncounterKillRosterTableColumn.ClassTalents,
              // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.ClassTalents),
              align: 'center',
              cellRender: ({ classTalents }) => {
                if (!classTalents) {
                  return <Typography.Text color="muted">-</Typography.Text>;
                }

                const MAX_TALENTS_TO_SHOW = 4;

                return (
                  <Stack wrap spacingInRems={2} justifyContent="center">
                    {classTalents.slice(-MAX_TALENTS_TO_SHOW).map((talent: any) => (
                      <TalentIcon talent={talent} size="small" key={talent.node.id} />
                    ))}
                  </Stack>
                );
              }
            },

            {
              header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.SpecTalents),
              accessor: EncounterKillRosterTableColumn.SpecTalents,
              // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.SpecTalents),
              align: 'center',
              cellRender: ({ specTalents }) => {
                if (!specTalents) {
                  return <Typography.Text color="muted">-</Typography.Text>;
                }

                const MAX_TALENTS_TO_SHOW = 4;

                return (
                  <Stack wrap spacingInRems={2} justifyContent="center">
                    {specTalents.slice(-MAX_TALENTS_TO_SHOW).map((talent: any) => (
                      <TalentIcon talent={talent} size="small" key={talent.node.id} />
                    ))}
                  </Stack>
                );
              }
            },

            ...(hasAnyHeroTalents
              ? ([
                  {
                    header: getEncounterKillRosterTableColumnLabel(EncounterKillRosterTableColumn.HeroTalents),
                    accessor: EncounterKillRosterTableColumn.HeroTalents,
                    // tooltip: getHeaderColumnTooltip(EncounterKillRosterTableColumn.HeroTalents),
                    align: 'center',
                    cellRender: ({ heroTalents }) => {
                      if (!heroTalents) {
                        return <Typography.Text color="muted">-</Typography.Text>;
                      }

                      const MAX_TALENTS_TO_SHOW = 4;

                      return (
                        <Stack wrap spacingInRems={2} justifyContent="center">
                          {heroTalents.slice(-MAX_TALENTS_TO_SHOW).map((talent: any) => (
                            <TalentIcon talent={talent} size="small" key={talent.node.id} />
                          ))}
                        </Stack>
                      );
                    }
                  }
                ] as TColumn<TTableRecord>[])
              : []),

            {
              header: <>&nbsp;</>,
              accessor: EncounterKillRosterTableColumn.LoadoutCalculator,
              cellRender: ({ character }, context) => {
                const { loadoutText } = character.talentLoadout || {};

                return (
                  <TalentLoadoutActionsMenu
                    loadoutText={loadoutText}
                    compareUrl={buildTalentCompareUrl(character)}
                    talentCalculatorUrl={loadoutText ? getWowheadTalentCalcUrl(loadoutText) : undefined}
                    characterUrl={buildCharacterUrl(character)}
                    findVideosUrl={
                      supportsFindVideosBySpec && difficulty && character.spec?.id
                        ? buildFindVideosFromSpecUrl(character)
                        : undefined
                    }
                    isExpanded={context.isExpanded}
                    onToggleExpand={context.isRowExpandable ? context.toggleExpand : undefined}
                  />
                );
              }
            }
          ] as TColumn<TTableRecord>[])
        : [])
    ];

    const embedHiddenColumns: EncounterKillRosterTableColumn[] = [
      EncounterKillRosterTableColumn.Index,
      EncounterKillRosterTableColumn.Videos
    ];

    const filteredColumns = baseColumns.filter((column) => {
      if (embed) {
        const isEmbedHiddenColumn = embedHiddenColumns.includes(column.accessor as EncounterKillRosterTableColumn);
        if (isEmbedHiddenColumn) {
          return false;
        }
      }

      const accessor = typeof column.accessor === 'function' ? column.accessor(data[0]) : column.accessor;

      if (accessor && accessor.toString().startsWith('tier')) {
        return true;
      }

      return visibleColumns.includes(column.accessor as EncounterKillRosterTableColumn);
    });

    return filteredColumns;
  }, [
    visibleColumns,
    embed,
    videos,
    validTiers,
    raid.slug,
    boss.encounterId,
    boss.slug,
    difficulty,
    supportsFindVideosBySpec,
    hasAnyDFLoadouts,
    hasAnyHeroTalents,
    bossKillKey,
    setGuildBossKillVideo
  ]);

  const data = useMemo(() => {
    return roster.map<TTableRecord>((member, index) => {
      const { classTalents, specTalents, heroTalents } = member.character.talentLoadout
        ? categorizeSpentLoadoutTalents(member.character.talentLoadout.specId!, member.character.talentLoadout.loadout, {
            showAll: false
          })
        : { classTalents: [], specTalents: [], heroTalents: [] };

      const specialItems = processCharacterSpecialItems(member.character.items?.items);

      return { ...member, specialItems, classTalents, specTalents, heroTalents, index };
    });
  }, [roster]);

  return (
    <div className={cx('slds-text-align--center styled-scrollbar', className)} style={{ overflowX: 'auto' }}>
      <DataTable
        columns={columns}
        data={data}
        className={cx('slds-table rio-rankings-table', {
          'slds-no-row-hover': !enableRowHover
        })}
        getRowKey={(row) => row.character.id.toString()}
        emptyContent="No data available"
        isLoading={isLoading}
        sorting={{
          default: [
            { id: 'role', desc: false },
            { id: 'character', desc: false }
          ]
        }}
        expandable={
          embed || expandable === false
            ? undefined
            : {
                icon: <i className="fa fa-fw fa-caret-right" />,
                isRowExpandable: () => true,
                getChildren: ({ character }) => (
                  <CharacterCallout
                    character={{ classId: character.class.id, specId: character.spec.id }}
                    items={character.items?.items}
                    talentLoadout={character.talentLoadout}
                    talentsDetails={character.talentsDetails}
                    interestingAuras={character.interestingAuras}
                    covenant={null}
                  />
                )
              }
        }
        {...restProps}
      />
    </div>
  );
}

function shouldShowVideo(
  videos: TRaidEncounterVideoListSummary[] | null,
  character: IRaidEncounterRosterMemberCharacter
) {
  if (!videos || !videos.length) {
    return false;
  }

  return videos.find((video) => video.character?.id === character.id);
}

interface IVideoColumnProps {
  videos: TRaidEncounterVideoListSummary[];
  character: IRaidEncounterRosterMemberCharacter;
  setGuildBossKillVideo?: (bossKillKey: string, video: TRaidEncounterVideoListSummary) => void;
  bossKillKey?: string;
}

function VideoColumn({ videos, character, setGuildBossKillVideo, bossKillKey }: IVideoColumnProps) {
  const video = shouldShowVideo(videos, character);

  const handleClick = () => {
    if (setGuildBossKillVideo && bossKillKey && video) {
      setGuildBossKillVideo(bossKillKey, video);
      const contentElem = document.getElementById('rio-encounter-tabs');
      if (contentElem) {
        contentElem.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {video && (
        <span className="slds-grid slds-grid--align-center rio-pointer" onClick={handleClick}>
          <i className="fa-solid fa-video-camera rio-hover-color-raiderio"></i>
        </span>
      )}
    </>
  );
}
